import { sectionHasSeat } from './section-capacity.js';
import { AppError } from '../errors/app-error.js';
export class Department {
    props;
    constructor(props) {
        this.props = props;
    }
    static create(params) {
        if (!params.name.trim())
            throw new Error('Department name cannot be empty');
        if (!/^[A-Za-z0-9-]+$/.test(params.code)) {
            throw new Error('Department code must be alphanumeric (hyphens allowed)');
        }
        const now = new Date();
        return new Department({
            id: params.id,
            tenantId: params.tenantId,
            name: params.name.trim(),
            code: params.code.toUpperCase(),
            ...(params.purpose !== undefined ? { purpose: params.purpose } : {}),
            ...(params.learningObjectives !== undefined
                ? { learningObjectives: params.learningObjectives }
                : {}),
            ...(params.primaryColor !== undefined ? { primaryColor: params.primaryColor } : {}),
            status: 'active',
            createdAt: now,
            updatedAt: now,
        });
    }
    static reconstitute(props) {
        return new Department(props);
    }
    get id() {
        return this.props.id;
    }
    get tenantId() {
        return this.props.tenantId;
    }
    get name() {
        return this.props.name;
    }
    get code() {
        return this.props.code;
    }
    get status() {
        return this.props.status;
    }
    get purpose() {
        return this.props.purpose;
    }
    get learningObjectives() {
        return [...(this.props.learningObjectives ?? [])];
    }
    get primaryColor() {
        return this.props.primaryColor;
    }
    get createdAt() {
        return this.props.createdAt;
    }
    get updatedAt() {
        return this.props.updatedAt;
    }
    update(params) {
        const next = {
            ...this.props,
            name: params.name?.trim() ?? this.props.name,
            code: params.code !== undefined ? params.code.toUpperCase() : this.props.code,
            status: params.status ?? this.props.status,
            ...(params.purpose !== undefined ? { purpose: params.purpose } : {}),
            ...(params.learningObjectives !== undefined
                ? { learningObjectives: params.learningObjectives }
                : {}),
            ...(params.primaryColor !== undefined ? { primaryColor: params.primaryColor } : {}),
            updatedAt: new Date(),
        };
        return new Department(next);
    }
    toSnapshot() {
        return { ...this.props };
    }
}
/** The named fallback for a version with no status, so the assumption is stated
 *  once instead of re-derived at each call site. */
export function curriculumVersionStatus(version) {
    return version.status === 'discontinued' ? 'discontinued' : 'active';
}
/**
 * The courses a program offers, taken from its curriculum — the authoritative
 * answer to "does this course belong to this program?" (director's decision,
 * 2026-09-14).
 *
 * It was not authoritative before, and the previous answers contradicted each
 * other: `Course.programId` says a course lives in ONE program (it is
 * `@deprecated` for that reason), while the section screen let any course be
 * offered under any program. Cross-listing is ordinary — a course required in
 * one program and elective in another — so the single field was the weak link,
 * not the section.
 *
 * ACROSS ALL VERSIONS, not only the current one: a program in teach-out still
 * offers the courses of the catalog its students were admitted under, and
 * refusing to open a section for them would break the very cohort the catalog
 * pinning exists to protect.
 *
 * An empty set means the program has no curriculum loaded — NOT that it has no
 * courses. Callers must tell those apart: a program with no malla cannot answer
 * the question, and the honest response is to allow and say so, never to deny.
 *
 * DISCONTINUED versions COUNT. A program in teach-out still has to open sections
 * for the courses its older cohort needs to graduate; excluding them would deny
 * exactly the students the catalog pin exists to protect.
 */
export function coursesInCurriculum(program) {
    const ids = new Set();
    for (const version of program.curriculumVersions ?? []) {
        for (const term of version.terms) {
            for (const id of term.recommendedCourseIds)
                ids.add(id);
        }
    }
    return ids;
}
/** Whether the program's curriculum declares this course. `false` when the
 *  program has no curriculum at all — check `hasCurriculum` first if the
 *  difference matters (it usually does). */
export function programIncludesCourse(program, courseId) {
    return coursesInCurriculum(program).has(courseId);
}
/** A program with at least one course declared in some catalog version. */
export function hasCurriculum(program) {
    return coursesInCurriculum(program).size > 0;
}
export class Program {
    props;
    constructor(props) {
        this.props = props;
    }
    static create(params) {
        if (params.durationTerms < 1)
            throw new Error('Duration must be at least 1 term');
        const now = new Date();
        return new Program({
            id: params.id,
            tenantId: params.tenantId,
            name: params.name,
            code: params.code,
            durationTerms: params.durationTerms,
            ...(params.description !== undefined ? { description: params.description } : {}),
            ...(params.departmentId !== undefined ? { departmentId: params.departmentId } : {}),
            status: 'active',
            documentRequirements: params.documentRequirements ?? [],
            reviewerConfig: [],
            createdAt: now,
            updatedAt: now,
        });
    }
    static reconstitute(props) {
        return new Program(props);
    }
    get id() {
        return this.props.id;
    }
    get tenantId() {
        return this.props.tenantId;
    }
    get name() {
        return this.props.name;
    }
    get code() {
        return this.props.code;
    }
    get status() {
        return this.props.status;
    }
    get durationTerms() {
        return this.props.durationTerms;
    }
    get reviewerConfig() {
        return [...this.props.reviewerConfig];
    }
    get noTuition() {
        return this.props.noTuition === true;
    }
    get feeConfig() {
        return this.props.feeConfig;
    }
    get capTarget() {
        return this.props.capTarget;
    }
    get description() {
        return this.props.description;
    }
    get purpose() {
        return this.props.purpose;
    }
    get learningObjectives() {
        return [...(this.props.learningObjectives ?? [])];
    }
    get departmentName() {
        return this.props.departmentName;
    }
    get departmentPurpose() {
        return this.props.departmentPurpose;
    }
    get departmentObjectives() {
        return [...(this.props.departmentObjectives ?? [])];
    }
    get philosophy() {
        return [...(this.props.philosophy ?? [])];
    }
    get policies() {
        return [...(this.props.policies ?? [])];
    }
    get latePolicy() {
        return this.props.latePolicy;
    }
    get modality() {
        return this.props.modality;
    }
    get technologyRequirements() {
        return this.props.technologyRequirements;
    }
    get minAttendancePct() {
        return this.props.minAttendancePct;
    }
    get researchRubric() {
        return this.props.researchRubric;
    }
    get rubrics() {
        return [...(this.props.rubrics ?? [])];
    }
    get primaryColor() {
        return this.props.primaryColor;
    }
    get departmentId() {
        return this.props.departmentId;
    }
    get curriculumVersions() {
        return [...(this.props.curriculumVersions ?? [])];
    }
    get courseEquivalences() {
        return [...(this.props.courseEquivalences ?? [])];
    }
    get currentCatalogYear() {
        return this.props.currentCatalogYear;
    }
    get stacksOnProgramId() {
        return this.props.stacksOnProgramId;
    }
    get enforceStackEntry() {
        return this.props.enforceStackEntry ?? false;
    }
    get includesStackedPrograms() {
        return this.props.includesStackedPrograms ?? false;
    }
    get enforcePrerequisites() {
        return this.props.enforcePrerequisites ?? false;
    }
    get minCoursesPerTerm() {
        return this.props.minCoursesPerTerm;
    }
    get offeringMode() {
        return this.props.offeringMode ?? 'curated_cohort';
    }
    update(params) {
        if (params.durationTerms !== undefined && params.durationTerms < 1) {
            throw new Error('Duration must be at least 1 term');
        }
        if (params.minAttendancePct !== undefined &&
            params.minAttendancePct !== null &&
            (params.minAttendancePct < 0 || params.minAttendancePct > 100)) {
            throw new Error('minAttendancePct must be between 0 and 100');
        }
        const newProps = {
            ...this.props,
            name: params.name ?? this.props.name,
            code: params.code ?? this.props.code,
            durationTerms: params.durationTerms ?? this.props.durationTerms,
            status: params.status ?? this.props.status,
            documentRequirements: params.documentRequirements ?? this.props.documentRequirements,
            reviewerConfig: params.reviewerConfig ?? this.props.reviewerConfig,
            ...(params.description !== undefined ? { description: params.description } : {}),
            ...(params.purpose !== undefined ? { purpose: params.purpose } : {}),
            ...(params.learningObjectives !== undefined
                ? { learningObjectives: params.learningObjectives }
                : {}),
            ...(params.departmentName !== undefined ? { departmentName: params.departmentName } : {}),
            ...(params.departmentPurpose !== undefined
                ? { departmentPurpose: params.departmentPurpose }
                : {}),
            ...(params.departmentObjectives !== undefined
                ? { departmentObjectives: params.departmentObjectives }
                : {}),
            ...(params.philosophy !== undefined ? { philosophy: params.philosophy } : {}),
            ...(params.policies !== undefined ? { policies: params.policies } : {}),
            ...(params.technologyRequirements !== undefined
                ? { technologyRequirements: params.technologyRequirements }
                : {}),
            ...(params.primaryColor !== undefined ? { primaryColor: params.primaryColor } : {}),
            ...(params.departmentId !== undefined ? { departmentId: params.departmentId } : {}),
            updatedAt: new Date(),
        };
        if (params.minAttendancePct !== undefined) {
            if (params.minAttendancePct === null)
                delete newProps.minAttendancePct;
            else
                newProps.minAttendancePct = params.minAttendancePct;
        }
        if (params.formTemplateId !== undefined) {
            if (params.formTemplateId === null) {
                delete newProps.formTemplateId;
            }
            else {
                newProps.formTemplateId = params.formTemplateId;
            }
        }
        if (params.feeConfig !== undefined) {
            if (params.feeConfig === null) {
                delete newProps.feeConfig;
            }
            else {
                newProps.feeConfig = params.feeConfig;
            }
        }
        if (params.noTuition !== undefined) {
            if (params.noTuition)
                newProps.noTuition = true;
            else
                delete newProps.noTuition;
        }
        if (params.latePolicy !== undefined) {
            if (params.latePolicy === null)
                delete newProps.latePolicy;
            else
                newProps.latePolicy = params.latePolicy;
        }
        if (params.modality !== undefined) {
            if (params.modality === null)
                delete newProps.modality;
            else
                newProps.modality = params.modality;
        }
        if (params.rubrics !== undefined) {
            newProps.rubrics = params.rubrics;
        }
        if (params.researchRubric !== undefined) {
            if (params.researchRubric === null)
                delete newProps.researchRubric;
            else
                newProps.researchRubric = params.researchRubric;
        }
        if (params.curriculumVersions !== undefined) {
            newProps.curriculumVersions = params.curriculumVersions;
        }
        if (params.courseEquivalences !== undefined) {
            newProps.courseEquivalences = params.courseEquivalences;
        }
        if (params.currentCatalogYear !== undefined) {
            if (params.currentCatalogYear === null)
                delete newProps.currentCatalogYear;
            else
                newProps.currentCatalogYear = params.currentCatalogYear;
        }
        if (params.enforcePrerequisites !== undefined) {
            newProps.enforcePrerequisites = params.enforcePrerequisites;
        }
        if (params.minCoursesPerTerm !== undefined) {
            if (params.minCoursesPerTerm === null)
                delete newProps.minCoursesPerTerm;
            else
                newProps.minCoursesPerTerm = params.minCoursesPerTerm;
        }
        if (params.offeringMode !== undefined) {
            newProps.offeringMode = params.offeringMode;
        }
        if (params.capTarget !== undefined) {
            if (params.capTarget === null)
                delete newProps.capTarget;
            else
                newProps.capTarget = params.capTarget;
        }
        if (params.enforceStackEntry !== undefined) {
            if (params.enforceStackEntry)
                newProps.enforceStackEntry = true;
            else
                delete newProps.enforceStackEntry;
        }
        if (params.includesStackedPrograms !== undefined) {
            if (params.includesStackedPrograms)
                newProps.includesStackedPrograms = true;
            else
                delete newProps.includesStackedPrograms;
        }
        if (params.stacksOnProgramId !== undefined) {
            if (params.stacksOnProgramId === null)
                delete newProps.stacksOnProgramId;
            else if (params.stacksOnProgramId === this.props.id)
                throw new Error('A program cannot build on itself');
            else
                newProps.stacksOnProgramId = params.stacksOnProgramId;
        }
        return new Program(newProps);
    }
    toSnapshot() {
        return { ...this.props };
    }
}
export class Term {
    props;
    constructor(props) {
        this.props = props;
    }
    static create(params) {
        if (params.endDate.isBefore(params.startDate)) {
            throw new Error('Term end date must be after start date');
        }
        const now = new Date();
        return new Term({ ...params, status: 'planned', createdAt: now, updatedAt: now });
    }
    static reconstitute(props) {
        return new Term(props);
    }
    get id() {
        return this.props.id;
    }
    get tenantId() {
        return this.props.tenantId;
    }
    get name() {
        return this.props.name;
    }
    get status() {
        return this.props.status;
    }
    get startDate() {
        return this.props.startDate;
    }
    get endDate() {
        return this.props.endDate;
    }
    get enrollmentDeadline() {
        return this.props.enrollmentDeadline;
    }
    get enrollmentWindow() {
        return this.props.enrollmentWindow;
    }
    get offeringModeOverride() {
        return this.props.offeringModeOverride;
    }
    update(params) {
        const newStart = params.startDate ?? this.props.startDate;
        const newEnd = params.endDate ?? this.props.endDate;
        if (newEnd.isBefore(newStart)) {
            throw new Error('Term end date must be after start date');
        }
        const next = {
            ...this.props,
            name: params.name ?? this.props.name,
            startDate: newStart,
            endDate: newEnd,
            status: params.status ?? this.props.status,
            ...(params.enrollmentDeadline !== undefined
                ? { enrollmentDeadline: params.enrollmentDeadline }
                : {}),
            updatedAt: new Date(),
        };
        if (params.enrollmentWindow !== undefined) {
            if (params.enrollmentWindow === null)
                delete next.enrollmentWindow;
            else
                next.enrollmentWindow = params.enrollmentWindow;
        }
        if (params.offeringModeOverride !== undefined) {
            if (params.offeringModeOverride === null)
                delete next.offeringModeOverride;
            else
                next.offeringModeOverride = params.offeringModeOverride;
        }
        return new Term(next);
    }
    open() {
        if (this.props.status !== 'planned')
            throw new Error('Can only open a planned term');
        return new Term({ ...this.props, status: 'open', updatedAt: new Date() });
    }
    toSnapshot() {
        return { ...this.props };
    }
}
export class Course {
    props;
    constructor(props) {
        this.props = props;
    }
    static create(params) {
        if (params.credits < 0)
            throw new Error('Credits cannot be negative');
        const now = new Date();
        return new Course({
            id: params.id,
            tenantId: params.tenantId,
            name: params.name,
            code: params.code,
            credits: params.credits,
            ...(params.programId !== undefined ? { programId: params.programId } : {}),
            ...(params.description !== undefined ? { description: params.description } : {}),
            ...(params.learningObjectives !== undefined
                ? { learningObjectives: params.learningObjectives }
                : {}),
            ...(params.anchorVerse !== undefined ? { anchorVerse: params.anchorVerse } : {}),
            ...(params.biblicalVersion !== undefined ? { biblicalVersion: params.biblicalVersion } : {}),
            ...(params.recommendedReadings !== undefined
                ? { recommendedReadings: params.recommendedReadings }
                : {}),
            prerequisites: params.prerequisites ?? [],
            ...(params.requirements !== undefined ? { requirements: params.requirements } : {}),
            status: 'active',
            createdAt: now,
            updatedAt: now,
        });
    }
    static reconstitute(props) {
        return new Course(props);
    }
    get id() {
        return this.props.id;
    }
    get tenantId() {
        return this.props.tenantId;
    }
    get name() {
        return this.props.name;
    }
    get code() {
        return this.props.code;
    }
    get credits() {
        return this.props.credits;
    }
    get description() {
        return this.props.description;
    }
    get learningObjectives() {
        return [...(this.props.learningObjectives ?? [])];
    }
    get anchorVerse() {
        return this.props.anchorVerse;
    }
    get biblicalVersion() {
        return this.props.biblicalVersion;
    }
    get recommendedReadings() {
        return [...(this.props.recommendedReadings ?? [])];
    }
    get prerequisites() {
        return [...this.props.prerequisites];
    }
    get requirements() {
        return this.props.requirements;
    }
    get status() {
        return this.props.status;
    }
    get programId() {
        return this.props.programId;
    }
    update(params) {
        if (params.credits !== undefined && params.credits < 0) {
            throw new Error('Credits cannot be negative');
        }
        const next = {
            ...this.props,
            name: params.name ?? this.props.name,
            code: params.code ?? this.props.code,
            credits: params.credits ?? this.props.credits,
            prerequisites: params.prerequisites ?? this.props.prerequisites,
            ...(params.requirements !== undefined ? { requirements: params.requirements } : {}),
            status: params.status ?? this.props.status,
            ...(params.description !== undefined ? { description: params.description } : {}),
            ...(params.learningObjectives !== undefined
                ? { learningObjectives: params.learningObjectives }
                : {}),
            ...(params.biblicalVersion !== undefined ? { biblicalVersion: params.biblicalVersion } : {}),
            ...(params.recommendedReadings !== undefined
                ? { recommendedReadings: params.recommendedReadings }
                : {}),
            updatedAt: new Date(),
        };
        if (params.anchorVerse !== undefined) {
            if (params.anchorVerse === null)
                delete next.anchorVerse;
            else
                next.anchorVerse = params.anchorVerse;
        }
        if (params.programId !== undefined) {
            if (params.programId === null)
                delete next.programId;
            else
                next.programId = params.programId;
        }
        return new Course(next);
    }
    toSnapshot() {
        return { ...this.props };
    }
}
export class Section {
    props;
    constructor(props) {
        this.props = props;
    }
    static create(params) {
        // 0 = SIN LÍMITE DE CUPO (section-capacity.ts), y así lo leen ya la
        // inscripción, la autoinscripción y los contadores (H7).
        if (params.maxEnrollment < 0)
            throw new Error('Max enrollment cannot be negative');
        const now = new Date();
        return new Section({
            ...params,
            programId: params.programId,
            schedule: params.schedule ?? [],
            currentEnrollment: 0,
            status: 'open',
            createdAt: now,
            updatedAt: now,
        });
    }
    static reconstitute(props) {
        return new Section(props);
    }
    get id() {
        return this.props.id;
    }
    get tenantId() {
        return this.props.tenantId;
    }
    get programId() {
        return this.props.programId;
    }
    get courseId() {
        return this.props.courseId;
    }
    get termId() {
        return this.props.termId;
    }
    get facultyId() {
        return this.props.facultyId;
    }
    get assistantIds() {
        return [...(this.props.assistantIds ?? [])];
    }
    /** ¿Esta persona ENSEÑA acá? Titular o ayudante, da lo mismo para ver el
     *  curso. La pregunta vive en la entidad porque la hacen el informe de
     *  trabajo del aula, la página del curso y el resumen semanal: repetida en
     *  cada una, tarde o temprano una diría que no donde las otras dicen que sí. */
    teachesHere(personId) {
        if (this.props.facultyId.getValue() === personId)
            return true;
        return (this.props.assistantIds ?? []).some((a) => a.getValue() === personId);
    }
    get maxEnrollment() {
        return this.props.maxEnrollment;
    }
    get currentEnrollment() {
        return this.props.currentEnrollment;
    }
    get status() {
        return this.props.status;
    }
    get schedule() {
        return [...this.props.schedule];
    }
    get startDate() {
        return this.props.startDate;
    }
    get endDate() {
        return this.props.endDate;
    }
    get gradesClosedAt() {
        return this.props.gradesClosedAt;
    }
    get gradesClosedBy() {
        return this.props.gradesClosedBy;
    }
    get lifecycleOverride() {
        return this.props.lifecycleOverride;
    }
    /**
     * Derive lifecycle state from dates + override. Mirrors the frontend
     * `deriveSectionLifecycle` so backend enforcement matches what the user sees.
     *
     * @param todayYmd Today as `yyyy-MM-dd` **already resolved in the tenant zone**
     *   (via `todayInZone`/`todayInZoneWeb`). The domain stays zone-agnostic: it does
     *   pure string comparison, so the caller owns the zone. A UTC "today" would flip
     *   the state a day early/late at the Chile boundary — hence not a `Date` here.
     *   Section `startDate`/`endDate` are stored at local noon, so their `ymd` is the
     *   intended calendar day regardless of the reader's zone.
     */
    deriveLifecycle(todayYmd) {
        const ARCHIVE_DAYS = 180;
        const ymd = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${dd}`;
        };
        const addDays = (d, days) => {
            const out = new Date(d);
            out.setDate(out.getDate() + days);
            return out;
        };
        const override = this.props.lifecycleOverride;
        if (override === 'concluded') {
            if (this.props.endDate && todayYmd > ymd(addDays(this.props.endDate, ARCHIVE_DAYS))) {
                return 'archived';
            }
            return 'concluded';
        }
        if (this.props.endDate && todayYmd > ymd(addDays(this.props.endDate, ARCHIVE_DAYS))) {
            return 'archived';
        }
        if (override === 'active')
            return 'active';
        if (this.props.startDate && todayYmd < ymd(this.props.startDate))
            return 'upcoming';
        if (this.props.endDate && todayYmd > ymd(this.props.endDate))
            return 'concluded';
        return 'active';
    }
    /**
     * Set or clear the lifecycle override.
     * Pass `null` to remove the override and fall back to date-based derivation.
     */
    setLifecycleOverride(override) {
        const next = new Section({
            ...this.props,
            updatedAt: new Date(),
        });
        const snap = next.toSnapshot();
        if (override === null)
            delete snap.lifecycleOverride;
        else
            snap.lifecycleOverride = override;
        return Section.reconstitute(snap);
    }
    /** Abierta y con lugar. Un cupo ≤ 0 = sin límite (ver section-capacity.ts). */
    hasAvailableSpot() {
        return (this.props.status === 'open' &&
            sectionHasSeat(this.props.currentEnrollment, this.props.maxEnrollment));
    }
    incrementEnrollment() {
        if (!this.hasAvailableSpot())
            throw new Error('Section is full or not open');
        return new Section({
            ...this.props,
            currentEnrollment: this.props.currentEnrollment + 1,
            updatedAt: new Date(),
        });
    }
    decrementEnrollment() {
        if (this.props.currentEnrollment <= 0)
            throw new Error('Enrollment count cannot be negative');
        return new Section({
            ...this.props,
            currentEnrollment: this.props.currentEnrollment - 1,
            updatedAt: new Date(),
        });
    }
    closeGradeBook(actorUid, at = new Date()) {
        if (this.props.status === 'grades_closed')
            throw new Error('Grade book already closed');
        return new Section({
            ...this.props,
            status: 'grades_closed',
            gradesClosedAt: at,
            ...(actorUid ? { gradesClosedBy: actorUid } : {}),
            updatedAt: new Date(),
        });
    }
    /** Vuelve a abrir el libro de notas de una sección cerrada.
     *
     *  SIN ESTO, CERRAR MAL ERA PERMANENTE — peor que el problema que el cierre
     *  venía a resolver. Es el tercer tercio del patrón estándar: el profesor
     *  entrega, el registro es dueño del acta, y **sólo el registro puede
     *  reabrir**. Ese control de acceso vive en el callable, no acá: el dominio
     *  cuida la transición, no quién la pide.
     *
     *  VUELVE A `open` Y NO AL ESTADO ANTERIOR, porque no lo guardamos: una
     *  sección `cancelled` no se cierra ni se reabre, y `closed` es un estado
     *  que hoy nadie escribe. Inventar un historial de estados para un caso que
     *  no existe sería complejidad sin dueño. */
    reopenGradeBook() {
        if (this.props.status !== 'grades_closed') {
            throw new Error('Grade book is not closed');
        }
        const { gradesClosedAt: _at, gradesClosedBy: _by, ...rest } = this.props;
        return new Section({ ...rest, status: 'open', updatedAt: new Date() });
    }
    update(params) {
        // 0 = sin límite de cupo (section-capacity.ts).
        if (params.maxEnrollment !== undefined && params.maxEnrollment < 0) {
            throw new Error('Max enrollment cannot be negative');
        }
        // Build props without optional dates first, then assign conditionally
        // to satisfy exactOptionalPropertyTypes (cannot assign `undefined` to optional prop)
        const updated = new Section({
            ...this.props,
            facultyId: params.facultyId ?? this.props.facultyId,
            maxEnrollment: params.maxEnrollment ?? this.props.maxEnrollment,
            status: params.status ?? this.props.status,
            programId: params.programId ?? this.props.programId,
            updatedAt: new Date(),
        });
        const snap = updated.toSnapshot();
        if (params.sectionCode !== undefined)
            snap.sectionCode = params.sectionCode;
        if (params.schedule !== undefined)
            snap.schedule = params.schedule.map((s) => ({ ...s }));
        if (params.assistantIds !== undefined) {
            // EL TITULAR NO ES SU PROPIO AYUDANTE. Dejarlo en las dos listas haría
            // que quitarlo de una no cambiara nada, y el día que cambie el titular
            // quedaría de ayudante de sí mismo sin que nadie lo decidiera.
            const sinTitular = params.assistantIds.filter((a) => a.getValue() !== snap.facultyId.getValue());
            const vistos = new Set();
            snap.assistantIds = sinTitular.filter((a) => {
                if (vistos.has(a.getValue()))
                    return false;
                vistos.add(a.getValue());
                return true;
            });
        }
        const newStart = 'startDate' in params ? params.startDate : this.props.startDate;
        const newEnd = 'endDate' in params ? params.endDate : this.props.endDate;
        if (newStart != null)
            snap.startDate = newStart;
        else
            delete snap.startDate;
        if (newEnd != null)
            snap.endDate = newEnd;
        else
            delete snap.endDate;
        return Section.reconstitute(snap);
    }
    toSnapshot() {
        return { ...this.props };
    }
}
export class Enrollment {
    props;
    constructor(props) {
        this.props = props;
    }
    static create(params) {
        const now = new Date();
        return new Enrollment({
            ...params,
            status: 'enrolled',
            enrolledAt: now,
            createdAt: now,
            updatedAt: now,
        });
    }
    static reconstitute(props) {
        return new Enrollment(props);
    }
    get id() {
        return this.props.id;
    }
    get studentId() {
        return this.props.studentId;
    }
    get sectionId() {
        return this.props.sectionId;
    }
    get termId() {
        return this.props.termId;
    }
    get status() {
        return this.props.status;
    }
    get grade() {
        return this.props.grade;
    }
    get gradeLabel() {
        return this.props.gradeLabel;
    }
    get gradeNote() {
        return this.props.gradeNote;
    }
    drop() {
        this.assertActive();
        return new Enrollment({
            ...this.props,
            status: 'dropped',
            droppedAt: new Date(),
            updatedAt: new Date(),
        });
    }
    /** RETIRO CON W (H8): la sección ya empezó, así que la inscripción queda en
     *  el expediente como «W» —no cuenta en el promedio ni en los créditos
     *  (`classifyAttempt`)—. Antes del inicio no se retira: se borra (sin rastro),
     *  y eso lo decide el caso de uso. */
    withdraw() {
        this.assertActive();
        return new Enrollment({
            ...this.props,
            status: 'dropped',
            gradeLabel: 'W',
            droppedAt: new Date(),
            updatedAt: new Date(),
        });
    }
    /** Solo una inscripción vigente se da de baja. Antes era un `Error` sin
     *  código, que el servidor convertía en un 500 (dar de baja una inscripción
     *  aprobada, o dos veces por un doble clic). */
    assertActive() {
        if (this.props.status !== 'enrolled') {
            throw AppError.validation('Can only drop an active enrollment', 'enrollment.drop_not_active', {
                status: this.props.status,
            });
        }
    }
    /** La nota final y, desde que hay una sola escala por seminario, su
     *  ETIQUETA tal como se escribe en el acta («5.5», «B+», «W»): es lo que lee
     *  el expediente. Sin etiqueta conserva la que tenía. */
    updateGrade(grade, gradeLabel) {
        return new Enrollment({
            ...this.props,
            grade,
            ...(gradeLabel !== undefined ? { gradeLabel } : {}),
            updatedAt: new Date(),
        });
    }
    close(status) {
        return new Enrollment({
            ...this.props,
            status,
            updatedAt: new Date(),
        });
    }
    toSnapshot() {
        return { ...this.props };
    }
}
export class Grade {
    props;
    constructor(props) {
        this.props = props;
    }
    static create(params) {
        const now = new Date();
        return new Grade({ ...params, gradedAt: now, createdAt: now, updatedAt: now });
    }
    static reconstitute(props) {
        return new Grade(props);
    }
    get id() {
        return this.props.id;
    }
    get enrollmentId() {
        return this.props.enrollmentId;
    }
    get studentId() {
        return this.props.studentId;
    }
    get sectionId() {
        return this.props.sectionId;
    }
    get value() {
        return this.props.value;
    }
    get scale() {
        return this.props.scale;
    }
    get isClosed() {
        return this.props.closedAt !== undefined;
    }
    get closedAt() {
        return this.props.closedAt;
    }
    update(value, gradedBy) {
        if (this.isClosed)
            throw new Error('Cannot update a closed grade');
        return new Grade({
            ...this.props,
            value,
            gradedBy,
            gradedAt: new Date(),
            updatedAt: new Date(),
        });
    }
    close(closedBy) {
        if (this.isClosed)
            throw new Error('Grade already closed');
        return new Grade({
            ...this.props,
            closedAt: new Date(),
            closedBy,
            updatedAt: new Date(),
        });
    }
    reopen() {
        const { closedAt, closedBy, ...rest } = this.props;
        return new Grade({
            ...rest,
            updatedAt: new Date(),
        });
    }
    toSnapshot() {
        return { ...this.props };
    }
}
export class ProgramEnrollment {
    props;
    constructor(props) {
        this.props = props;
    }
    static create(params) {
        const now = new Date();
        return new ProgramEnrollment({
            id: params.id,
            tenantId: params.tenantId,
            studentId: params.studentId,
            programId: params.programId,
            intakeTermId: params.intakeTermId,
            ...(params.expectedGraduationTermId !== undefined
                ? { expectedGraduationTermId: params.expectedGraduationTermId }
                : {}),
            ...(params.catalogYear !== undefined ? { catalogYear: params.catalogYear } : {}),
            ...(params.notes !== undefined ? { notes: params.notes } : {}),
            status: 'active',
            startedAt: now,
            createdAt: now,
            updatedAt: now,
        });
    }
    static reconstitute(props) {
        return new ProgramEnrollment(props);
    }
    get id() {
        return this.props.id;
    }
    get tenantId() {
        return this.props.tenantId;
    }
    get studentId() {
        return this.props.studentId;
    }
    get programId() {
        return this.props.programId;
    }
    get intakeTermId() {
        return this.props.intakeTermId;
    }
    get expectedGraduationTermId() {
        return this.props.expectedGraduationTermId;
    }
    get catalogYear() {
        return this.props.catalogYear;
    }
    get status() {
        return this.props.status;
    }
    get notes() {
        return this.props.notes;
    }
    /** Editable advisor fields. Status changes go through the transition methods. */
    update(params) {
        const next = { ...this.props, updatedAt: new Date() };
        if (params.expectedGraduationTermId !== undefined) {
            if (params.expectedGraduationTermId === null)
                delete next.expectedGraduationTermId;
            else
                next.expectedGraduationTermId = params.expectedGraduationTermId;
        }
        if (params.catalogYear !== undefined) {
            if (params.catalogYear === null)
                delete next.catalogYear;
            else
                next.catalogYear = params.catalogYear;
        }
        if (params.notes !== undefined) {
            if (params.notes === null)
                delete next.notes;
            else
                next.notes = params.notes;
        }
        return new ProgramEnrollment(next);
    }
    markOnLeave() {
        if (this.props.status !== 'active') {
            throw new Error(`Can only put an active program enrollment on leave (was ${this.props.status})`);
        }
        return new ProgramEnrollment({ ...this.props, status: 'on_leave', updatedAt: new Date() });
    }
    reactivate() {
        if (this.props.status !== 'on_leave') {
            throw new Error(`Can only reactivate an on-leave program enrollment (was ${this.props.status})`);
        }
        return new ProgramEnrollment({ ...this.props, status: 'active', updatedAt: new Date() });
    }
    markGraduated(graduationTermId) {
        if (this.props.status === 'withdrawn') {
            throw new Error('Cannot graduate a withdrawn program enrollment');
        }
        if (this.props.status === 'on_leave') {
            // Entrega 4: a paused student must be reactivated before graduating, so
            // the record never jumps on_leave → graduated silently.
            throw new Error('Reactivate the program enrollment before graduating it');
        }
        const now = new Date();
        return new ProgramEnrollment({
            ...this.props,
            status: 'graduated',
            endedAt: now,
            ...(graduationTermId ? { expectedGraduationTermId: graduationTermId } : {}),
            updatedAt: now,
        });
    }
    withdraw(reason) {
        if (this.props.status === 'graduated') {
            throw new Error('Cannot withdraw a graduated program enrollment');
        }
        const now = new Date();
        return new ProgramEnrollment({
            ...this.props,
            status: 'withdrawn',
            endedAt: now,
            ...(reason ? { endReason: reason } : {}),
            updatedAt: now,
        });
    }
    toSnapshot() {
        return { ...this.props };
    }
}
//# sourceMappingURL=academic.entity.js.map